import * as React from 'react';
import { FaInfoCircle } from 'react-icons/fa';

import { mapKeys } from 'lodash/fp';

import ServicesLink from '@portal/common/components/ServicesLink';
import conditionDetailNotes from '@portal/common/locale/condition-detail-notes';
import causes from '@portal/common/locale/en/causes';
import common from '@portal/common/locale/en/common';
import sequelas from '@portal/common/locale/en/sequelas';
import {
    AGE_GROUP_ID_KEY,
    ANTIBIOTIC_CLASS_ID_KEY,
    APPARENT_ID_KEY,
    CONDITION_LEVEL_KEY,
    CONSTANT_VACCINATION_ID_KEY,
    COUNTERFACTUAL_ID_KEY,
    CRO_ID_KEY,
    DAY_KEY,
    EDUCATION_ID_KEY,
    EXPANDED_WOLBACHIA_ID_KEY,
    FORECAST_SCENARIO_ID_KEY,
    GENDER_ID_KEY,
    INFECTION_TYPE_ID_KEY,
    INFECTIOUS_SYNDROME_ID_KEY,
    LOCATION_ID_KEY,
    OUTCOME_ID_KEY,
    PATHOGEN_ID_KEY,
    PAYER_ID_KEY,
    PRIMARY_ENTITY_ID_LOCALIZATION_KEY,
    RACE_ID_KEY,
    SEVERITY_ID_KEY,
    SYMPTOMATIC_ID_KEY,
    TOC_ID_KEY,
    VACCINATION_STATUS_ID_KEY,
    VACCINE_STATUS_ID_KEY,
    VEHSS_CONDITION_ID_KEY,
    YEAR_KEY,
} from '@portal/common/models/data-key';
import {
    AntimicrobialResistanceRecords,
    CroRecords,
    ExpenditureRecords,
    HealthThreatsMetricsRecords,
    RaceEthnicityEyeConditionsRecords,
    TakedaDengueRecords,
} from '@portal/common/models/data-type';

import {
    COVID_19_EXPLORER_PATH,
    DATA_EXPLORER_PATH,
    DENGUE_SCENARIOS_PATH,
    EDP_EXPLORER_PATH,
    HEALTH_THREATS_METRICS_PATH,
    MORBIDITY_EXPLORER_PATH,
    SIMULATION_EXPLORER_PATH,
} from '../router/paths';

const infoIcon = <FaInfoCircle />;

export default {
    // portal common
    ...common,

    ...conditionDetailNotes,

    // merged causes and sequela into one dictionary for data explorer special case
    // it gives following formats: cause_sequela_cause_XXX | cause_sequela_sequela_XXX
    ...mapKeys((key) => 'cause_sequela_' + key, causes),
    ...mapKeys((key) => 'cause_sequela_' + key, sequelas),

    // Form fields
    form_field_first_name: 'First Name',
    form_field_last_name: 'Last Name',
    form_field_email: 'Email Address',
    form_field_title: 'Title',
    form_field_password: 'Password',
    form_field_old_password: 'Old Password',
    form_field_new_password: 'New Password', // @deprecated We are switching to password/old_password instead of new_password/password
    form_field_new_password_confirmation: 'Confirm New Password',
    form_field_organization_role: 'Role',
    organization_has_programmatic_access: 'Programmatic Access',
    organization_has_programmatic_access_false: 'Not Allowed',
    organization_has_programmatic_access_true: 'Allowed',

    // Table Headers
    table_header_name: 'Name',
    table_header_job_title: 'Job Title',
    table_header_email: 'Email',
    table_header_organization_role: 'Role',
    table_header_actions: 'Actions',
    table_header_category: 'Category',
    table_header_created: 'Date Added',
    table_header_last_access: 'Last Access',
    table_header_added: 'Date Added',
    table_header_updated: 'Date Updated',
    table_header_icon: ' ',

    // Errors
    error_user_has_no_organization:
        "We're sorry but you cannot access this site. Your user is not a member of organization.",
    error_disabled_organization:
        "We're sorry but you cannot access this site. Your organization is disabled.",
    error_only_csv: 'File: Only .csv file types are supported.',
    error_no_valid_emails:
        'File: No email addresses were found. Please upload a CSV file containing valid email addresses.',
    error_not_valid_csv_file: 'File: Not valid csv file.',
    error_field_is_required: 'This field is required',
    error_short_password: 'Password should be at least 8 characters long',
    error_required_field: "This field can't be empty",
    error_invalid_email: 'Invalid email format',
    error_passwords_must_match: 'Passwords have to match',
    error_server: 'Server error',

    error_limit_exceeded:
        'The maximum number of filters has been exceeded. Reduce the number of selected filters and try again.', // do not edit this message it's coming from the server

    // Navigation
    nav_group_main: 'Main',
    nav_group_data: 'Data',
    nav_group_tools: 'IHME Tools',
    nav_group_tools_minimized: 'Tools',
    nav_group_ihme: 'IHME',
    nav_group_organization: 'Organization',

    nav_dashboard: 'Dashboard',
    nav_my_favorites: 'Favorite Content',
    nav_custom_chart: 'Data Explorer',
    nav_covid_19_data_explorer: 'Covid-19 Explorer',
    nav_covid_19_testing_protocol: 'Covid-19 Testing Protocol',
    nav_morbidity_explorer: 'Multimorbidity Explorer',
    nav_edp_explorer: 'Early Detection Program',
    nav_simulation_explorer: 'Simulation Tool',
    nav_health_threats_tool: 'Global Health Threats Tool',
    nav_dengue_scenarios_tool: 'Dengue Scenarios',
    nav_gbd_tools: 'GBD Tools',
    nav_gbd_2017_compare: 'GBD 2017 Compare',
    nav_gbd_2017_search: 'GBD 2017 Results',
    nav_cod_viz: 'CodViz',
    nav_epi_viz: 'EpiViz',
    nav_disease_atlas: 'Disease Atlas',
    nav_us_disease_expenditure: 'US Disease Expenditure',
    nav_background_info: 'Background Info',
    nav_sources_methodology: 'Sources & Methodology',
    nav_definitions: 'Definitions',
    nav_sources: 'Data Sources',
    nav_methodology: 'Methodology',
    nav_staff: 'IHME CSU Staff',
    nav_contact_us: 'Contact Us',
    nav_profile: 'My Profile',
    nav_team: 'My Group',
    nav_sign_out: 'Sign Out',

    // TeamScene
    team_scene_title: 'My Group',
    team_invite_member: 'Invite Member',
    team_bulk_invite_members: 'Bulk Invite (CSV)',
    team_self_registration_options: 'Self Registration',
    team_delete_modal_title: 'Delete Member',
    delete_member_modal_message: (
        <span>
            <p>
                Are you sure? <br />{' '}
            </p>
            This user will be permanently deleted. Any resources they created will also be deleted.
        </span>
    ),

    // TeamScene
    content_scene_title: 'Group Content',

    // DataExplorerScene
    custom_chart_scene_title: 'Data Explorer',
    covid_19_explorer_title: 'Covid-19 Explorer',
    covid_19_testing_protocol_title: 'Covid-19 Testing Protocol',
    morbidity_explorer_title: 'Multimorbidity Explorer',
    edp_explorer_title: 'Early Detection Program',
    simulation_explorer_title: 'Multiple Myeloma Simulation Explorer',
    health_threats_explorer_title: 'Global Health Threats Tool',
    dengue_scenarios_explorer_title: 'Dengue Scenarios',

    custom_chart_scene_data_table_section_title: 'Data Table',
    custom_chart_scene_charts_section_title: 'Chart',
    custom_chart_scene_condition_detail_notes_title: 'Condition Detail Notes',
    chart_type_label_line: 'Timeline',
    chart_type_label_map: 'Map',
    chart_type_label_bar: 'Bar Chart',
    chart_type_label_treemap: 'Treemap',
    chart_type_label_arrow: 'Arrow Chart',
    chart_type_label_scatter_map: 'Map',
    chart_type_label_table: 'Data Table',
    line_chart_regular_scenario: 'Regular Scenario',
    line_chart_better_scenario: 'Better Scenario',
    line_chart_worse_scenario: 'Worse Scenario',
    display_uncertainty: 'Display uncertainty?',
    display_forecasts: 'Show Forecasts?',
    display_forecasts_levels: 'Show forecast levels?',
    display_top_locations: 'Show top locations list?',
    show_outcomes_averted: 'Averted',
    show_cumulative_outcomes: 'Cumulative',
    disabled_dengue_averted_calculations_tooltip:
        'In order to see averted outcomes, you need to select other than Baseline scenario.',
    disabled_dengue_cumulative_calculations_tooltip:
        'Cumulative results are not calculated for rate-space.',
    loading_baseline_data_for_dengue_calculations_tooltip:
        'Please wait while baseline data required for calculations is loaded.',
    map_chart_zoom_controls_label: 'ZOOM IN/OUT',
    bar_chart_clear_selection: 'Clear Selection',
    bar_chart_display_labels: 'Include data labels',
    bar_chart_uncertainty_checkbox_tooltip:
        'Visualization of uncertainty values is not supported for stacked bar charts.',
    bar_chart_labels_checkbox_tooltip:
        'There is not enough room to display values on the chart. Try reducing the scope of your filters.',
    data_explorer_data_type_locked_explanation:
        'Due to differences in data display and scale, causes, sequela, and granular resolution are the only Types that can be combined in a search. Other searches are limited by Type.',
    data_explorer_copy_api_request_label: 'API Request',
    data_explorer_copied_api_request_label: 'Copied!',
    arrow_chart_choose_another_year_to_compare: 'Choose another year to compare',

    ihme_citation_text:
        'Institute for Health Metrics and Evaluation (IHME) at the University of Washington, accessed on: %date%. Used with permission. ',

    citing_ihme_data_title: 'Citing IHME data',
    citing_ihme_data_general_intro: 'For general citation purposes, you may cite:',
    citing_ihme_data_portal_link_label: 'IHME Client Portal',
    citing_ihme_data_gbd_projections_note:
        'Global Burden of Disease estimates are published through 2023; estimates beyond 2023 are projections.',
    citing_ihme_data_methodology_citation:
        'Institute for Health Metrics and Evaluation (IHME) at the University of Washington. Accessed %date% from the IHME Client Portal. Used with permission. Methodology outlined in %reference%',
    citing_ihme_data_gbd_heading: 'To cite the Global Burden of Disease Study methodology:',
    citing_ihme_data_gbd_reference:
        'Hay SI, Ong KL, Santomauro DF, et al. Global burden of 375 diseases and injuries, risk-attributable burden of 88 risk factors, and healthy life expectancy in 204 countries and territories, including 660 subnational locations, 1990–2023: a systematic analysis for the Global Burden of Disease Study 2023. The Lancet. 2025;406:1873–1922.',
    citing_ihme_data_gbd_link_label:
        'Burden of 375 diseases and injuries, risk-attributable burden of 88 risk factors, and healthy life expectancy in 204 countries and territories, including 660 subnational locations, 1990–2023: a systematic analysis for the Global Burden of Disease Study 2023',
    citing_ihme_data_forecast_heading: 'To cite the forecast methodology:',
    citing_ihme_data_forecast_reference:
        'Vollset SE, Ababneh HS, Abate YH, et al. Burden of disease scenarios for 204 countries and territories, 2022–2050: a forecasting analysis for the Global Burden of Disease Study 2021. The Lancet. 2024;403:2204–2256.',
    citing_ihme_data_forecast_link_label:
        'Burden of disease scenarios for 204 countries and territories, 2022–2050: a forecasting analysis for the Global Burden of Disease Study 2021',
    citing_ihme_data_race_ethnicity_heading:
        'To cite the methodology for U.S. estimates by race and ethnicity:',
    citing_ihme_data_race_ethnicity_reference:
        'Dwyer-Lindgren L, Kendrick P, Kelly YO, et al. Cause-specific mortality by county, race, and ethnicity in the USA, 2000–19: a systematic analysis of health disparities. The Lancet. 2023;402:1065–1082.',
    citing_ihme_data_race_ethnicity_link_label:
        'Cause-specific mortality by county, race, and ethnicity in the USA, 2000–19: a systematic analysis of health disparities',

    custom_chart_scene_na_treemap_chart: 'Treemap chart is not applicable for selected data type.',
    custom_chart_scene_sequela_data_na: 'Treemap chart is not applicable for Sequela data type.',
    custom_chart_scene_treemap_feedback: (
        <>
            We are testing the tree map functionality. We would love to hear your feedback. Tell us
            what you like or would improve while using this chart: <ServicesLink />
        </>
    ),

    add_to_list_name_your_selection: 'How would you like to name this favorite content?',
    select_data_basic_search: 'Basic Search',
    select_data_advanced_search: 'Advanced Search',
    select_data_apply_filters: 'Run Query',
    run_query_button_disabled_tooltip:
        'The scope of data is too large to query.\nTry reducing the number of filter selections.',

    // Notifications
    notification_new_gbd_becomes_default_title: 'Global Burden of Disease updates now available!',
    notification_new_gbd_becomes_default_message: (Link) => (
        <ul style={{ listStyle: 'initial' }}>
            <li>
                A <b>Global Burden of Disease (GBD)</b> update with trends from 1990-2024 is now
                your default data suite.
            </li>
            <li>
                The Methodology selection in the side menu provides links to the latest GBD
                publications and methodology appendices.
            </li>
            <li>
                The prior GBD with forecasts continues to be available by switching views in the
                Data Suite dropdown. <Link>Explore</Link> your GBD license now.
            </li>
        </ul>
    ),
    // EDP data explorer
    edp_data_explorer_filter_data: 'Filter Data',
    edp_data_select_indicators: 'Select Indicators',
    edp_indicator_button_label: 'Add indicator',

    // Simulation Explorer
    simulation_tool_0_header: 'Executive Summary',
    simulation_tool_0_dynamic_text_6:
        'In 2019, an estimated 55.55k people were living with MM in China.',
    simulation_tool_0_dynamic_text_102:
        'In 2019, an estimated 89.57k people were living with MM in the US.',
    simulation_tool_0_text: (HeaderComponent, dynamicText) => (
        <>
            <p>
                Multiple Myeloma (MM) is an incurable disease causing significant health burden
                worldwide. {dynamicText} However, a variety of life-extending treatment options
                exist. As the treatment landscape for MM evolves, Sanofi is interested in previewing
                potential directions for its new anti-CD38 drug therapy, isatuximab-irfc
                (isatuximab), over the next few years. Specifically, the study explorable in this
                tool estimates the potential population-level uptake rate and health impact of
                isatuximab usage in the US and China over the next five years, under multiple ‘what
                if’ scenarios.
            </p>
            <p>
                Simulation studies provide a powerful option to address complex treatment questions.
                In the case of MM, there is much uncertainty regarding how to best treat
                patients-especially around the selection of subsequent lines of treatment after
                frontline therapy loses effectiveness. With patients often undergoing 3 or more
                lines of treatment, and with many different treatment options available at each
                stage, head-to-head treatment-sequencing clinical trials become less practical.
            </p>
            <p>
                Simulation studies such as this one, estimating patient volumes under various
                scenarios and the population-level impact of isatuximab, can inform strategies to
                increase uptake of isatuximab in the population. The <b>analytic approach</b>{' '}
                demonstrated here can also be adapted to other cancers with complex treatment
                pathways. Our simulation platform is built to draw on detailed population-level
                estimates stratified by age, sex, time, and location, with the capacity to further
                incorporate external data as needed to mirror relevant attributes of real-world
                populations.
            </p>
            <p>
                This work demonstrates the flexibility of the simulation platform for further use
                and development, given additional input data and locations of interest.
            </p>
            <HeaderComponent>Topline findings</HeaderComponent>
            <ul>
                <li>
                    <b>FINDING 1:</b> In the US, isatuximab treated patients are projected to have
                    slightly improved outcomes relative to non-monoclonal antibody treated patients
                    in the newly diagnosed multiple myeloma (NDMM) setting. In China the projected
                    differences are even smaller.
                </li>
                <li>
                    <b>FINDING 2:</b> Scale-up of isatuximab frontline treatment to 14% in the US
                    and 19.23% in China dramatically increases annual patients treated, compared to
                    both baseline and isatuximab following daratumumab scenarios for both locations.
                    <ul>
                        <li>
                            In the US we estimate a 177% increase in patients treated with
                            isatuximab across all lines by 2027.
                        </li>
                        <li>
                            In China, we estimate a 91% increase in patients treated with isatuximab
                            across all lines by 2027.
                        </li>
                    </ul>
                </li>

                <li>
                    <b>FINDING 3:</b> The scale-up of isatuximab in early lines of treatment does
                    not decrease its use in later lines of treatment in the US or in China.
                </li>
            </ul>
        </>
    ),

    simulation_tool_1_header: 'Background: %location% population and health',
    download_sim_report_button: 'Download full technical report',
    download_button: 'Download',
    simulation_location_dropdown_placeholder: 'Select location...',

    // ---
    simulation_tool_1_1_title: 'Understand the status of population health in %location%.',

    simulation_tool_estimated_death_metric: 'estimated deaths per year',
    simulation_tool_hale_metric: ({ Link }) => (
        <>
            <Link>health-adjusted life expectancy</Link> for 50-54-year-olds
        </>
    ),
    simulation_tool_haq_metric: ({ Link }) => (
        <>
            place in global ranking of <Link>healthcare access and quality index</Link>
        </>
    ),
    simulation_tool_sdi_metric: ({ Link }) => (
        <>
            place in global ranking of <Link>socio-demographic index</Link>
        </>
    ),

    simulation_tool_line_chart_title:
        'Figure 1: Estimated death rate in %location% by year %yearsRange%, age standardized. Future projections based on "business as usual", better, and worse case scenarios.',

    simulation_tool_1_2_title: 'Explore population characteristics in %location%.',

    simulation_tool_1_2_description_1:
        'In 2019, %currentPopulation% of %location%’s population was aged 75 years or older. This number is expected to increase to %population2030% by 2030 and %population2050% by 2050.',
    simulation_tool_1_2_description_2:
        'Life expectancy at birth is projected to increase from %currentLifeExpectancy% in 2019 to %projectedLifeExpectancy% years by 2050.',
    simulation_tool_1_2_image_title:
        'Figure 2: Stacked estimated population by age in %location% by sex and year, 2020 - 2050',
    simulation_tool_1_2_description_3:
        'Multiple myeloma is an incurable disease, but a variety of life-extending treatment options exist. This tool presents topline findings and summarizes the methods of a simulation estimating potential population-level impact of isatuximab usage on patient outcomes.',
    simulation_tool_1_2_chart_title: 'Age structure in %location% by sex and year, 1990 - 2100',

    // ---
    simulation_tool_2_header: 'Background: Multiple Myeloma in %location%',
    simulation_tool_2_description: ({
        location,
        prevalence,
        deaths,
        ylls,
        rank2019,
        dalyNumber2019,
        rank1990,
        dalyRankChange,
        dalyNumberChange,
        highestRateAgeGroup,
        highestRateValue,
    }) => (
        <React.Fragment>
            <p>
                In 2019, {prevalence} people were living with multiple myeloma (MM) in {location}.
                Among all neoplasms in {location}, multiple myeloma ranked {rank2019} in terms of
                DALYs incurred in 2019, causing the loss of {dalyNumber2019} years of healthy life
                among the population that year. This ranking has {dalyRankChange} from {rank1990} in
                1990. Total DALYs caused by multiple myeloma has {dalyNumberChange} over the past
                decade. In 2019 multiple myeloma caused {deaths} deaths and {ylls} years of life
                lost (YLLs).
            </p>
            <p>
                As MM is largely considered a disease of the elderly, with the average onset between
                70 to 75 years of age, its prevalence has been projected to increase in the future,
                as the global population continues to age and live longer due to modern medicine and
                other life improving factors. The {highestRateAgeGroup} year old age group
                experienced the highest rate of multiple myeloma deaths, at {highestRateValue} per
                100,000.
            </p>
        </React.Fragment>
    ),

    simulation_tool_mm_death_metric: 'multiple myeloma deaths per year',
    simulation_tool_projected_deaths_metric: 'projected deaths in 2050 based on current trajectory',
    simulation_tool_cancer_prevalence_metric: 'most prevalent cancer in %location%',
    simulation_tool_new_cases_metric: 'new cases per year in %location%',
    simulation_tool_treemap_chart_title:
        'Figure 3: Multiple myeloma situated within all cancers, size proportional to number of DALYs incurred by both sexes and all ages in %location% in %year%',

    // ---
    simulation_tool_3_header: 'Study methods',
    simulation_tool_3_1_title: 'About the study',
    simulation_tool_3_1_description: ({ GBDLink, DownloadReportLink }) => (
        <React.Fragment>
            <p>
                The simulation, run by the Institute for Health Metrics and Evaluation (IHME) at the
                University of Washington, explored isatuximab uptake under a baseline treatment
                scenario and two alternative scenarios. It estimated survival endpoints following
                isatuximab, daratumumab, and other treatments. Each of the three scenarios was
                simulated both in the United States and China.
            </p>
            <p>
                The simulation provides Sanofi with results based on current best evidence, to aid
                in long-term strategic planning. This work showcases the flexibility of the
                simulation platform for further use and development, given additional input data and
                locations of interest. Input sources include{' '}
                <GBDLink>the Global Burden of Disease study (GBD) 2019</GBDLink>, Flatiron Health,
                literature searches, and Sanofi’s expert guidance. Detailed methods of the study are
                available in the Technical Report available for{' '}
                <DownloadReportLink>download</DownloadReportLink> above. Below follows an
                abbreviated description.
            </p>
        </React.Fragment>
    ),

    simulation_tool_3_3_title: 'IHME simulation background',
    simulation_tool_3_3_description: (
        <React.Fragment>
            <p>
                To enable faithful scenario estimation and uncertainty quantification for health
                interventions, IHME's simulation
            </p>
            <ul>
                <li>is an individual-based, discrete-time, Monte Carlo simulation system.</li>
                <li>
                    creates an ensemble of simulated populations that mirror the real-world
                    population of interest.
                </li>
            </ul>
            <p>
                Each simulated individual, called a <b>simulant</b>, is assigned all attributes
                relevant to the study question: age, sex, disease status, risk factors, etc. By
                default, these attrubutes are assigned based on their estimated prevalence and
                uncertainty in the GBD study; but external data can be incorporated as needed.
            </p>

            <p>IHME’s simulations account for two major types of uncertainty:</p>
            <ul>
                <li>
                    <b>Parameter uncertainty</b> reflects the limits of our knowledge of true
                    population rates (e.g. disease prevalence and mortality) and of the exact
                    efficacy of interventions.
                </li>
                <li>
                    <b>Stochastic uncertainty reflects</b> the fact that even if population-level
                    rates and effects were knowable, individual outcomes still vary. Therefore,
                    finite-sized samples of individuals exhibit variability.
                </li>
            </ul>
            <p>
                The ensemble of simulated populations allows for the quantification of uncertainty,
                with single populations viewed as draws from Bayesian-derived joint posterior
                distributions. The same population ensemble is used in each scenario, thus reducing
                unnecessary variability when comparing scenario outcomes.
            </p>
        </React.Fragment>
    ),

    simulation_tool_3_4_title: 'Scenarios',
    simulation_tool_baseline_scenario: 'Baseline scenario',
    simulation_tool_baseline_scenario_location_102: (
        <>
            <p>
                This scenario reflects current regulatory approvals, current practice, and
                business-as-usual expectations, as described by Sanofi experts.
            </p>
            <p>
                Isatuximab is eligible for use from the second line of treatment (LoT) onwards.
                However, in our baseline scenario it is disallowed <b>immediately</b> following a
                LoT containing daratumumab. This daratumumab-specific restriction does not reflect
                any regulatory policy, but rather expert assumptions about clinical practice.
            </p>
            <p>
                Under the baseline scenario, the proportion of patients receiving isatuximab is
                linearly ramped up to line-specific target treatment coverage rates by 2028.
                Demographic characteristics and population rates are derived from GBD 2019.
            </p>
        </>
    ),

    simulation_tool_baseline_scenario_location_6: (
        <>
            <p>
                This scenario reflects current regulatory approvals, current practice, and
                business-as-usual expectations, as described by Sanofi experts.
            </p>
            <p>
                Isatuximab is not approved in China yet. Sanofi projects that an approval will be
                granted soon. However, in our baseline scenario it will remain disallowed{' '}
                <b>immediately</b> following a LoT containing daratumumab. This daratumumab-specific
                restriction does not reflect any regulatory policy, but rather expert assumptions
                about clinical practice.
            </p>
            <p>
                Under the baseline scenario, the proportion of patients receiving isatuximab is
                linearly ramped up (starting in 2024) to line-specific target treatment coverage
                rates by 2028. Demographic characteristics and population rates are derived from GBD
                2019.
            </p>
        </>
    ),
    simulation_tool_alternative_scenario_1: 'Alternative scenario 1',
    simulation_tool_alternative_scenario_1_location_102: (
        <p>
            The same as the baseline scenario, except that isatuximab is allowed as a treatment
            option directly after daratumumab <b>frontline</b> (LoT 1) treatment. Specifically, if a
            simulant receives daratumumab in LoT 1, they have a 5% probability of receiving
            isatuximab in LoT 2.
        </p>
    ),
    simulation_tool_alternative_scenario_1_location_6: (
        <p>
            The same as the baseline scenario, except that isatuximab is allowed as a treatment
            option directly after daratumumab <b>frontline</b> (LoT 1) treatment. Specifically, if a
            simulant receives daratumumab in LoT 1, they have a 5% probability of receiving
            isatuximab in LoT 2.
        </p>
    ),
    simulation_tool_alternative_scenario_2: 'Alternative scenario 2',
    simulation_tool_alternative_scenario_2_location_102: (
        <p>
            The same as the baseline scenario, except that isatuximab is also approved and adopted
            as frontline treatment. Frontline coverage starts ramping up in 2021. By 2025
            approximately 10% of simulants receive an isatuximab-containing LoT 1, linearly ramping
            up to 14% by 2028.
        </p>
    ),
    simulation_tool_alternative_scenario_2_location_6: (
        <p>
            The same as the baseline scenario, except that isatuximab is also approved and adopted
            as frontline treatment starting in 2024. By 2028 approximately 19.2% of simulants
            receive an isatuximab-containing LoT 1.
        </p>
    ),
    simulation_tool_both_countries_title: 'Both countries',
    simulation_tool_both_countries_text:
        'Additional specific rates for all scenarios can be found in the treatment coverage scale-up section of the Technical Report.',

    simulation_tool_3_5_title: 'Concept model',
    simulation_tool_3_5_description: (
        <>
            <p>
                The concept model (Figure 4) is an accurate but simplified blueprint for the
                simulation, describing how risk factors, disease, and interventions relate to one
                another in the simulated environment. The model and its main components (covariates,
                risk factors, causes, health system) are described in detail on pages 26-48 of the
                accompanying Technical Report.
            </p>
        </>
    ),
    simulation_tool_3_5_image_title:
        'Figure 4: Concept model diagram presenting a high-level blueprint of the simulation structure.',

    simulation_tool_3_6_title: 'Data sources',
    simulation_tool_3_6_description: ({ GBDLink, NCCNLink }) => (
        <>
            <br />
            <p>About the individual sources:</p>

            <ul>
                <li>
                    <GBDLink>The Global Burden of Disease (GBD)</GBDLink> is the most comprehensive
                    worldwide epidemiological study to date, estimating the burden of over 350
                    causes of death and disability related to thousands of conditions for every
                    country globally. The study also generates demographic (age, sex) information.
                    All disease estimates from the study are age- and sex-specific. This simulation
                    used GBD 2019, the most recent GBD version at the time of model development.
                </li>
                <li>
                    Flatiron Health is an oncology-focused electronic health record (EHR) company.
                    The simulation used a Flatiron Health microdata dataset of US MM patients,
                    facilitated by Sanofi. Challenges encountered by IHME and Sanofi with this data
                    source, and our approach to resolve them, are described in pages 23-25 of the
                    accompanying Technical Report.
                </li>
                <li>
                    RCTs: for some key inputs for which Flatiron data was revealed as not
                    sufficiently reliable, we conducted an independent literature search and network
                    meta-analysis of randomized control trials (RCTs).
                </li>
                <li>
                    <NCCNLink>NCCN</NCCNLink> is the National Comprehensive Cancer Network, a
                    nonprofit alliance of leading US-based cancer centers.
                </li>
            </ul>
        </>
    ),

    // ---
    simulation_tool_4_header: 'Simulation results',
    simulation_tool_4_1_title: 'Survival findings',
    simulation_tool_4_1_description_1_location_102:
        'Isatuximab treated patients have slightly improved outcomes relative to non-monoclonal antibody treated patients in the newly diagnosed multiple myeloma (NDMM) setting.',
    simulation_tool_4_1_image_title_1:
        'Figure 5: %location% relapse-free survival in the NDMM setting for treatment line 1 in alternative scenario 2.',
    simulation_tool_4_1_image_title_2_location_102:
        'Figure 6: US relapse-free survival in the relapsed and refractory multiple myeloma setting for treatment line 2 in alternative scenario 2.',
    simulation_tool_4_1_data_table_title:
        'Table 2: Treatment outcomes by population subgroups, %location%.',

    simulation_tool_4_1_description_1_location_6: (
        <p>
            The simulation suggested a minimal difference in population-level survival outcomes
            associated with a 19.2% scale-up of frontline isatuximab coverage by the beginning of
            2028.
        </p>
    ),

    simulation_tool_4_1_description_2_location_6:
        'With isatuximab projected to enter the Chinese market in 2024 as a frontline therapy, the time period simulated is too limited to capture any effect by 2028 of isatuximab usage on survival results. Additionally, there is increased stochastic uncertainty due to the lower MM prevalence in China. Therefore we only present NDMM setting results for China.',

    simulation_tool_4_2_title: 'Treatment coverage findings',
    simulation_tool_4_2_image_title:
        'Figure 7: Example treatment sequencing in the simulated population (lines 1 - 3).',
    simulation_tool_4_2_description_1_location_6: (
        <div>
            <b>FINDING 1:</b> Scale-up of isatuximab frontline treatment to 19.23% in China
            dramatically increases annual patients treated, compared to both baseline and isatuximab
            following daratumumab scenarios for China. Topline findings are presented here; the
            accompanying Technical Report contains more detailed analysis.
        </div>
    ),
    simulation_tool_4_2_description_1_location_102: (
        <div>
            <b>FINDING 1:</b> Scale-up of isatuximab frontline treatment to 14% in the US
            dramatically increases annual patients treated, compared to both baseline and isatuximab
            following daratumumab scenarios for United States. Topline findings are presented here;
            the accompanying Technical Report contains more detailed analysis.
        </div>
    ),
    simulation_tool_4_2_description_location_102: (
        <ul>
            <li>
                Total number of patients treated in alternative scenario 2 (Isa frontline) increases
                by <b>155%</b> from 2,668 (95% UI: 1,250 – 4,500) in 2023 to 6,796 (4,710 – 10,060)
                in 2027.
            </li>

            <li>
                Scale-up of frontline isatuximab treatment yields a <b>177%</b> increase in total
                annual patients treated between baseline and alternative scenario 2. 2,027 (1,238 –
                3,787) patients are treated in 2027 under baseline compared to 6,796 (4,710 –
                10,060) alternative scenario 2.
            </li>
            <li>
                5% isatuximab treatment directly after daratumumab treatment yields an <b>8%</b>{' '}
                difference in total annual patients treated between baseline and alternative
                scenario 1. 2,455 (1,238 – 3,787) patients are treated in 2027 under baseline
                compared to 2,648 (1,239 – 4,186) under alternative scenario 1.
            </li>
        </ul>
    ),

    simulation_tool_4_2_description_location_6: (
        <ul>
            <li>
                Total number of patients treated increases from 0 in 2023 to 6,684 (95% UI: 1,092 –
                13,613) in 2028. There is a <b>641%</b> increase across all lines between 2023 and
                2027.
            </li>
            <li>
                Scale-up of frontline isatuximab treatment yields a <b>91%</b> increase in total
                annual patients treated between baseline and alternative scenario 2. 3,491 (0 –
                7,650) patients are treated in 2027 under baseline compared to 6,684 (1,092 –
                13,613) under alternative scenario 2.
            </li>
            <li>
                5% isatuximab treatment directly after daratumumab treatment yields a <b>3%</b>{' '}
                increase in total annual patients treated between baseline and alternative scenario
                1. 3,491 (0 – 7,650) patients are treated in 2027 under baseline and 3,606 (0 –
                8,060) under alternative scenario 1. These uncertainty intervals begin at 0 due to
                stochastic uncertainty introduced by lower MM prevalence in China.
            </li>
        </ul>
    ),
    simulation_tool_4_2_description_2: (
        <div>
            <b>FINDING 2:</b> The scale-up of isatuximab in early lines of treatment does not
            decrease its use in later lines of treatment in the US or China. In other words, the
            increase in isatuximab usage in the first line of treatment in alternative scenario 2
            did not substantially decrease its use in subsequent lines of treatment.
        </div>
    ),

    simulation_tool_4_3_title: 'Future directions',
    simulation_tool_4_3_description: (
        <>
            <p>
                IHME has built a highly flexible simulation platform whose input parameters and
                logic can be updated and expanded. Further iterations or phases of this simulation
                could lead to updated results based on additional isatuximab data, additional
                complexity, as well as results for more locations of interest.
            </p>
            <p>
                There is uncertainty regarding how to best treat MM patients, especially regarding
                optimal treatment sequencing given so many different options. In this context, where
                head-to-head clinical trials are not practical, simulation studies can address
                complex treatment questions. Our analytic approach, informed by both clinical trial
                data and existing real-world data, adds to the emerging effort to simulate
                treatment-related outcomes for rare cancers with complex treatment pathways.
            </p>
        </>
    ),

    // SelfRegistrationOptionsModal
    self_sign_up_options_title: 'Self Registration Options',
    self_sign_up_mode_label: 'Self registration mode',
    self_sign_up_token_label: 'Self registration URL',
    self_sign_up_copy_link: 'Copy to clipboard',
    self_sign_up_regenerate_link: 'Regenerate URL',
    self_sign_up_mode_none: 'Not Allowed',
    self_sign_up_mode_email: 'Email Based',
    self_sign_up_mode_link: 'URL Based',
    email_domains_label: 'Email domains',
    email_domains_hint:
        'Enter a comma-separated list of email domains that members of this Organization should belong to in order to be allowed to be invited. Leave empty for no restriction.',

    modal_close: 'Close',
    modal_done: 'Done',

    // Profile Scene
    profile_header: 'Profile & Settings',
    profile_general_tab: 'General',
    profile_change_password_tab: 'Change Password',
    profile_api_access_tab: 'API Access',
    profile_connected_apps_tab: 'Connected Apps',
    profile_sign_out_tab: 'Log out',
    profile_save_changes: 'Save Changes',
    profile_first_name: 'First Name',
    profile_last_name: 'Last Name',
    profile_email: 'Email Address',
    profile_title: 'Job Title',
    profile_avatar_url: 'Avatar',

    data_license_title: 'Data Collections',

    // Change Password Tab
    change_password_title: 'Change Password',
    change_password_button: 'Change Password',

    // Connected Apps Tab
    connected_apps_description:
        'AI apps you allowed to use the IHME Client Portal on your behalf, for example through the IHME Portal MCP server. Disconnecting an app signs it out right away.',
    connected_apps_table_column_name: 'App',
    connected_apps_table_column_last_used: 'Last Used',
    connected_apps_table_column_disconnect: 'Disconnect',
    connected_apps_table_empty:
        'No AI apps are connected. When you connect the IHME Portal MCP server in an app such as Claude or Cursor, it appears here.',
    connected_apps_disconnect_modal_title: 'Disconnect App',
    connected_apps_disconnect_modal_message:
        '%client% will lose access right away. To use it again, connect it from the app.',
    connected_apps_disconnect_button: 'Disconnect',
    connected_apps_load_error: 'Connected apps could not be loaded. Reload the page to try again.',

    // OAuth Consent Scene
    oauth_consent_heading: '%client% wants to use your IHME Client Portal account',
    oauth_consent_switch_account: 'Switch',
    oauth_consent_switch_account_label: 'Switch account',
    oauth_consent_permissions_title: 'It will be able to',
    oauth_consent_scope_search_metadata: 'Search IHME metadata',
    oauth_consent_scope_read_licensed_data: 'Read the data your organization is licensed for',
    oauth_consent_limits:
        'It can’t see your password or change your account, organization or saved content.',
    oauth_consent_name_warning:
        'The app chose the name “%client%” itself. Only allow this if you started the connection from that app.',
    oauth_consent_return_to: ({ redirect }) => <>You’ll return to {redirect}.</>,
    oauth_consent_redirect_local: 'an app on this computer',
    oauth_consent_revoke_hint: ({ Link }) => (
        <>
            Disconnect any time in <Link>Profile & Settings › Connected Apps</Link>
        </>
    ),
    oauth_consent_allow: 'Allow access',
    oauth_consent_deny: 'Deny',
    oauth_consent_returning_title: 'Returning you to %target%',
    oauth_consent_returning_message: 'You can close this tab if it stays open.',
    oauth_consent_not_allowed:
        'Your account cannot connect AI apps. Connecting needs an active account in an organization with a current data license, and is not available while impersonating a user. You can still deny the request.',
    oauth_consent_expired_title: 'This connection request has expired',
    oauth_consent_expired_message:
        'Connection requests are valid for 10 minutes and work once. Go back to your AI app and connect again.',
    oauth_consent_error_title: 'Something went wrong',
    oauth_consent_error_message: 'Go back to your AI app and connect again.',

    // API Access Tab
    api_access_programmatic_toggle_label: 'Programmatic access:',
    api_access_create_button_label: 'Create Access Key',
    api_access_view_docs_button: 'View Documentation',
    api_credentials_table_column_access_key: 'Access Key',
    api_credentials_table_column_secret_key: 'Secret Key',
    api_credentials_table_column_created: 'Created',
    api_credentials_table_column_accessed: 'Last Accessed',
    api_credentials_table_empty_disabled:
        'API access is disabled. You can enable access by toggling on the Programmatic Access switch above.',
    api_credentials_table_empty:
        'There are no API access keys available. You can create a key by selecting the Create Access Key button above.',
    api_credentials_created_modal_title: 'Create Access Key',
    api_credentials_created_modal_message:
        'This is the only time that the secret access key can be viewed. You cannot recover them later. However, you can create new access keys at any time.',
    api_credentials_delete_modal_title: 'Delete Access Key',
    api_credentials_delete_modal_message: (
        <span>
            <p>
                Are you sure? <br />{' '}
            </p>
            This Access Key will be permanently deleted.
        </span>
    ),
    // EditPictureModal
    edit_picture_add_title: 'Add Picture',
    edit_picture_edit_title: 'Update Picture',
    edit_picture_add_button: 'Add Picture',
    edit_picture_update_button: 'Update Picture',
    edit_picture_url: 'File',

    // HomeScene
    license_is_expired: 'License is expired',
    license_for_organization_is_expired:
        'The commercial license under which %organization% has been accessing and using IHME health data is expired.',
    contact_us_for_new_license: (Link) => (
        <>
            <Link>Contact Us</Link> to renew your license.
        </>
    ),
    license_will_expire_soon: 'License expires in %days% days',
    explore_data_section_header: 'Explore Data',
    news_and_announcements_section_header: 'News and Announcements',
    cancel_confirmation_question: 'Are you sure you want to cancel?',
    cancel_confirmation_title: 'Cancel download',
    cancel_confirmation_button: 'Confirm',

    file_upload_input_choose_file_button_label: 'Choose File',
    file_upload_input_upload_button_label: 'Upload a new file',
    file_upload_input_cancel_button_label: 'Cancel upload',
    file_upload_input_overwrite_warning: 'WARNING: This action will overwrite the old file',

    staff_scene_title: 'IHME Client Services Staff',

    // Sign In scene
    sign_in_scene_register_link: 'New User? Click here to register.',
    sign_in_scene_sign_in_button: 'Sign In',
    sign_in_scene_sign_in_with_sso_button: 'Continue with SSO',
    sign_in_scene_forgot_password_link: 'Forgot password?',

    // AuthCallback Scene
    please_resign_in: ({ Link }) => (
        <React.Fragment>
            Please, try to&nbsp;<Link>sign in</Link>&nbsp;again.
        </React.Fragment>
    ),

    // Self Registration Scene
    registration_scene_resend_title: 'Registration Form',
    registration_scene_resend_email: 'Resend my sign up email.',

    // Resend Sign Up Email
    resend_sign_up_email_title: 'Resend Sign Up Email',
    resend_sign_up_email_success: 'Your sign up email has been sent. Please check your inbox.',
    resend_sign_up_email_resend: 'Resend',

    // CustomDataTableScene
    custom_data_table_title: 'Custom Data Tables',

    // MethodologyScene
    methodology_scene_title: 'Methodology',
    methodology_column_title: 'Reference material',
    methodology_column_description: 'Description',

    // Data Explorer
    data_explorer_condition_dropdown_placeholder:
        'To find your best match, search by condition name or ICD code...',
    data_explorer_condition_dropdown_placeholder_with_pharma_search:
        'To find your best match, search by condition name, pharma product, or ICD code...',
    data_explorer_condition_dropdown_placeholder_short: 'Search conditions...',

    /**
     * Please try to keep it grouped by the scene as above
     */
    empty_label_default_text: '-',

    country_forecasting_regular: 'Regular Scenario',
    country_forecasting_better: 'Better Scenario',
    country_forecasting_worse: 'Worse Scenario',

    forgot_password_reset: 'Password reset',
    forgot_password_success_reset:
        'You have been sent a new password. Please log in with your new password.',

    forgot_password_title: 'Reset Password',
    forgot_password_reset_button: 'Reset',
    forgot_password_description:
        'To reset your password, please confirm your email address below.\n\nYou will receive an email with a new password, after which you can go back and log in again.',

    global_ok: 'Ok',

    account_email: 'Email',
    account_password: 'Password',
    account_confirm_password: 'Confirm Password',
    account_first_name: 'First Name',
    account_last_name: 'Last Name',
    account_title: 'Title',
    account_role: 'Role',
    account_role_member: 'Member',
    account_role_admin: 'Admin',
    account_status: 'Status',
    account_generate_password_button: 'Generate password',

    contact_us_scene_title: 'Contact Us',
    contact_us_reason_suggestion: 'Suggestion',
    contact_us_reason_question: 'Data request or question',
    contact_us_reason_bug: 'Report a bug',
    contact_us_reason: 'Reason',
    contact_us_subject: 'Subject',
    contact_us_body: 'Comment',
    contact_us_is_manager_copied: 'Copy my Organization Admin(s)',
    contact_us_success_message: 'Thank you for contacting us',

    organization_member_add_title: 'Add member',
    organization_member_edit_title: 'Edit member',
    organization_member_role: 'Role',
    organization_member_select_role: 'Select role',
    organization_member_role_member: 'Member',
    organization_member_role_manager: 'Manager',

    save_button: 'Save',
    reset_button: 'Reset',
    okay_button: 'Okay',
    explore_now_button: 'Explore Now',

    app_name: 'IHME Client Portal',
    sign_in: 'Sign In',
    email: 'Email',
    forgot_password_question: 'Forgot password?',
    ie_compatibility_notice:
        'We are working to resolve some technical issues with how the Client Portal functions in Internet Explorer. If you are using IE, please try opening this link in another browser.',
    register_submit: 'Register',
    register_success:
        'Registration successful. Please check your inbox for a verification email and click on activation link in order to activate your account.',
    register_confirmation_title: 'Registration Confirmation',
    register_confirmation: 'Your account is activated, please ',
    register_confirmation_link: 'continue to the sign in page.',
    register_confirmation_already_activated: 'If your account is already activated, please ',
    register_confirmation_network_error:
        'We couldn’t reach the server to activate your account. Check your internet connection and try again.',
    register_confirmation_error: 'Your account could not be activated. Please try again later.',
    register_confirmation_invalid_link:
        'This activation link is invalid or incomplete. Please open the link from your activation email again.',
    register_confirmation_retry: 'Try Again',

    methodology_title: 'IHME Methodology',
    global_forecasting_title: 'Global Forecasting Map',
    country_forecasting_title: 'Country Forecasting Tool',
    news_title: "What's New",
    news_empty_message: 'There are no News & Announcements yet.',

    global_first_name: 'First Name',
    global_last_name: 'Last Name',
    global_role: 'Role',
    global_search_placeholder: 'Search',

    staff_invite_members: 'Invite Members',

    role_admin: 'Team Admin',
    role_member: 'Member',
    role_manager: 'Manager',

    profile_update_picture: 'Update Picture',
    profile_email_address: 'Email Address', // I think it's not used?

    lorem_ipsum:
        'Lorem ipsum dolor sit amet, ad dolorem fierent neglegentur eam, qui ne labores menandri scriptorem, soleat integre et eam. Nibh efficiendi vis et, ne dicta volutpat quaerendum sed, agam democritum adipiscing an pro. Quod verear nec ad. Ad diam latine patrioque per, ipsum apeirian definitionem usu cu. Enim meis aperiam vix at, aliquip singulis ius no. Ei liber quidam delicatissimi pri, quis semper denique ei mel, enim dicam an per.',

    filter_age_group_title: 'Age',
    filter_gender_title: 'Sex',
    filter_measure_title: 'Measure',
    filter_metric_title: 'Unit',
    filter_cause_title: 'Cause',
    filter_location_title: 'Location',

    data_collection_dropdown_label: 'Data Collection',
    data_collections_empty_dropdown_info:
        "Your organization does not currently have an active data collection with IHME. If you believe this is in error please contact your organization's administrator for more information.",
    data_collections_dropdown_license_info:
        'Data access is based on your organization’s agreement with IHME.',
    condition_type_dropdown_label: 'Types',
    data_collections_create_new_dropdown_option: 'Create a New Data Collection (Coming Soon)',

    refinement_condition_label: 'Conditions',
    refinement_types_label: 'Types',
    refinement_button_label: 'Add Refinement',
    refinement_filter_year_undefined: 'All Years',
    refinement_filter_day_undefined: 'All Days',
    refinement_filter_include_nowcasts: 'Include Pre-published estimates',

    export_single_age_estimates_label: 'Export for Custom Ages',
    export_single_age_modal_title: 'Custom Ages Report',
    export_single_age_file_name: 'Single-Year Ages Report',
    export_custom_age_band_file_name: 'Custom Ages Report',
    export_single_age_range_slider_title: 'Select years range',
    export_single_report_type_selector_label: 'Select one',
    export_single_age_modal_submit_label: 'Generate report',
    export_single_age_modal_loading_text: 'Generating your report. This may take a minute or two.',
    export_single_age_disabled_tooltip_label:
        '%unsupported% %verb% not supported for single age data interpolation. Supported: %supported%.',
    export_single_age_no_data_tooltip_label:
        'Single age data interpolation is not available for the selected %filter%: %unsupported%.',
    export_single_age_modal_hint_text:
        '%unsupported% %verb% not supported for single age data interpolation. Report will include only %supported%.',
    export_single_age_modal_warning_conditions_have_not_modeled_age_groups: `The following conditions: %conditions% are not modeled by IHME for all age groups; custom age aggregates outside modeled bounds are calculated with the assumption that age-specific disease rates and numbers outside of modeled age groups are 0. View modeled age ranges in Condition Detail notes.`,
    export_single_report_type_normal: 'Single Ages',
    export_single_report_type_aggregated: 'Aggregate',
    export_single_report_add_another_aggregate_btn: 'Add',
    export_single_report_selected_aggregates_dropdown_label: 'Aggregates currently selected',
    export_single_report_selected_aggregates_dropdown_placeholder: 'No age groups selected yet...',
    export_single_report_start_label: 'Start',
    export_single_report_end_label: 'End',
    export_single_age_restricted_range_tooltip:
        'Age ranges 0-0, 0-1, 0-2, 0-3 ("Under 1/2/3/4") or ranges starting with "1 to X" or "2 to X" are restricted for %measures%',

    refinement_day: 'Date Range',

    refinement_round_id_re_reporting_version: 'Race-Ethnicity Reporting Version',

    filter_label_cause_sequela_: 'Cause / Sequela',
    filter_label_round_id: 'Data Suite',
    filter_label_forecast_scenario_id: 'Forecast Scenario',
    filter_label_gender_id: 'Sex',
    filter_label_primary_entity_id: 'GBD Id',
    filter_label_underlying_condition: 'Underlying',
    filter_label_day: 'Day',
    filter_label_comorbid_cause_id: 'Comorbid',
    filter_label_month_count: 'Study (Months)',
    filter_label_cases: 'Cases',
    filter_label_age_group_range: 'Age Range',
    filter_label_data_type_id: 'Data Type',
    filter_label_primary_entity_localization_key: 'Condition',
    filter_label_risk_exposure_id: 'Exposure',
    [`filter_label_${CONDITION_LEVEL_KEY}`]: 'Data Type Level',
    [`filter_label_${APPARENT_ID_KEY}`]: 'Apparent',
    [`filter_label_${SYMPTOMATIC_ID_KEY}`]: 'Symptomatic',
    [`filter_label_${OUTCOME_ID_KEY}`]: 'Dengue case outcome',
    [`filter_label_${SEVERITY_ID_KEY}`]: 'Severity',
    [`filter_label_${INFECTION_TYPE_ID_KEY}`]: 'Infection Type',
    [`filter_label_${VACCINATION_STATUS_ID_KEY}`]: 'Vaccination Status',
    [`filter_label_${PAYER_ID_KEY}`]: 'Payer',
    [`filter_label_${TOC_ID_KEY}`]: 'Type Of Care',

    filter_label_race_id: 'Race',
    filter_label_education_id: 'Education',
    filter_label_facility_id: 'Facility',
    filter_label_industry_id: 'Industry',
    filter_label_response_id: 'Response',
    filter_label_ethnicity_id: 'Ethnicity',
    filter_label_indicator_id: 'Indicator',
    filter_label_employment_cohort_id: 'Employment Cohort',
    filter_label_employment_status_id: 'Employment Status',
    filter_label_portal_organization_id: 'Portal Organization',
    filter_label_infectious_syndrome_id: 'Infectious Syndrome',
    filter_label_antibiotic_class_id: 'Antibiotic Class',
    filter_label_counterfactual_id: 'Counterfactual',
    filter_label_pathogen_id: 'Pathogen',
    filter_label_case_definition: 'Case definition',
    filter_label_non_fatal_icd9_codes: 'ICD9 codes (non-fatal, all)',
    filter_label_non_fatal_icd10_codes: 'ICD10 codes (non-fatal, all)',
    filter_label_fatal_icd9_codes: 'ICD9 codes (fatal)',
    filter_label_fatal_icd10_codes: 'ICD10 codes (fatal)',

    data_license_type_cause_outcome: 'Cause Data',
    data_license_type_sequela_outcome: 'Sequela Data',
    data_license_type_risk_attribution: 'Risk Burden Data',
    data_license_type_risk_prevalence: 'Risk Prevalence Data',
    data_license_type_injury_records: 'Injury Data',
    data_license_type_risk_exposure_records: 'Risk Exposure Data',
    data_license_type_etiology_records: 'Etiology Data',
    data_license_type_impairment_records: 'Impairment Data',
    data_license_type_sev_records: 'SEV Data',
    data_license_type_covariate_records: 'Covariate Data',
    data_license_type_sdoh_records: 'SDoH Data',
    data_license_type_population_records: 'Population Data',
    data_license_type_life_expectancy_records: 'Life Expectancy Data',
    data_license_type_covid19_records: 'Covid-19 Data',
    data_license_type_cause_coprevalence_records: 'Current Co-Prevalence',
    data_license_type_projected_comorbidity_causes_outcome_records: 'Projected New Cases',
    data_license_type_cause_associated_coprevalence_records: 'Associated Current Co-Prevalence',
    data_license_type_cause_associated_comorbidity_study_coefficients:
        'Associated Projected New Cases',
    data_license_type_early_detection_onetime_indicators: 'Early Detection Onetime Indicators',
    data_license_type_early_detection_timeline_indicators: 'Early Detection Timeline Indicators',
    [`data_license_type_${HealthThreatsMetricsRecords}`]: 'Health Threats Metrics Data',
    [`data_license_type_${RaceEthnicityEyeConditionsRecords}`]:
        'Race & Ethnicity Eye Conditions Data',
    [`data_license_type_${AntimicrobialResistanceRecords}`]: 'Antimicrobial Resistance Data',
    [`data_license_type_${ExpenditureRecords}`]: 'Expenditure Data',
    [`data_license_type_${TakedaDengueRecords}`]: 'Takeda Dengue Data',
    [`data_license_type_${CroRecords}`]: 'Granular Resolution Data',

    data_license_measure_id: 'Measures',
    data_license_metric_id: 'Units',
    data_license_gender_id: 'Sexes',
    data_license_age_group_id: 'Age Groups',
    data_license_cause_id: 'Causes',
    data_license_cro_id: 'Granular Resolutions',
    data_license_comorbid_cause_id: 'Comorbid Causes',
    data_license_location_id: 'Locations',
    data_license_forecast_scenario_id: 'Forecast Scenarios',
    data_license_round_id: 'Data Suites',
    data_license_year: 'Years',
    data_license_month_count: 'Duration Of Projection (Months)',
    data_license_day: 'Days',
    data_license_sequela_id: 'Sequelas',
    data_license_covariate_id: 'Covariates',
    data_license_covariate_records: 'Covariates',
    data_license_sdoh_id: 'SDoHs',
    data_license_sdoh_records: 'SDoHs',
    data_license_sev_records: 'SEVs',
    data_license_impairment_records: 'Impairments',
    data_license_etiology_records: 'Etiologies',
    data_license_risk_attribution: 'Risks',
    data_license_risk_prevalence: 'Risks',
    data_license_race_id: 'Races',
    data_license_injury_records: 'Injuries',
    data_license_risk_exposure_records: 'Risks',
    data_license_risk_exposure_id: 'Exposures',
    [`data_license_${PATHOGEN_ID_KEY}`]: 'Pathogens',
    [`data_license_${ANTIBIOTIC_CLASS_ID_KEY}`]: 'Antibiotic Classes',
    [`data_license_${INFECTIOUS_SYNDROME_ID_KEY}`]: 'Infectious Syndromes',
    [`data_license_${COUNTERFACTUAL_ID_KEY}`]: 'Counterfactuals',
    [`data_license_${EDUCATION_ID_KEY}`]: 'Educations',
    [`data_license_${VEHSS_CONDITION_ID_KEY}`]: 'Eye Conditions',
    [`data_license_${PAYER_ID_KEY}`]: 'Payers',
    [`data_license_${TOC_ID_KEY}`]: 'Types Of Care',
    [`data_license_${APPARENT_ID_KEY}`]: 'Apparents',
    [`data_license_${INFECTION_TYPE_ID_KEY}`]: 'Infection Types',
    [`data_license_${OUTCOME_ID_KEY}`]: 'Dengue Case Outcomes',
    [`data_license_${SEVERITY_ID_KEY}`]: 'Severities',
    [`data_license_${SYMPTOMATIC_ID_KEY}`]: 'Symptomatics',
    [`data_license_${VACCINATION_STATUS_ID_KEY}`]: 'Vaccination Statuses',

    cause_search: 'Search...',
    sequela_search: 'Search...',
    rei_search: 'Search...',
    covariate_search: 'Search...',
    sdoh_search: 'Search...',

    forecast_scenario_short_1: 'Reference',
    forecast_scenario_short_2: 'Better',
    forecast_scenario_short_3: 'Worse',

    footer_note: (
        <span>
            Questions/Comments? Contact <ServicesLink />
        </span>
    ),

    cdn_show_more: 'Show More',
    cdn_show_less: 'Show Less',
    cdn_none: 'None',
    cdn_no_codes_for_modeling: 'No codes used for modeling',
    measure_id: 'Measure',
    cdn_condition: 'Condition',
    parent_condition: 'Parent condition',
    cdn_ages_not_modeled: 'All ages not modeled',
    cdn_locations_not_modeled: 'Globally not modeled',

    chart_legend_value: '(Value)',
    chart_legend_upper: '(Upper)',
    chart_legend_lower: '(Lower)',
    chart_legend_forecast_scenario_0_value: '(Past Estimate)',
    chart_legend_forecast_scenario_1_value: '(Reference)',
    chart_legend_forecast_scenario_2_value: '(Better forecast)',
    chart_legend_forecast_scenario_3_value: '(Worse forecast)',

    pdf_report_gbd_title: 'GBD Data Report',
    pdf_email_report_gbd_title: 'GBD Sample Data Report',
    pdf_report_covid_19_title: 'Covid-19 Data Report',
    pdf_report_morbidity_title: 'Multimorbidity Report',
    pdf_report_lung_cancer_title: 'Extended Lung Cancer Data Report',
    pdf_report_covid_19_testing_protocol_title: 'Covid-19 Testing Protocol Data Report',
    pdf_report_edp_explorer_title: 'Early Detection Program Data Report',
    pdf_report_health_threats_explorer_title: 'Global Health Threats Data Report',
    pdf_report_dengue_scenarios_explorer_title: 'Dengue Scenarios Data Report',
    pdf_report_data_table_too_large:
        'The selected data table is too large to download as a PDF export. You can export the data table separately as a CSV or Excel export.',
    refinement_filters_refine_selection_warning:
        'In order to display data, further refine your selection below.',
    refinement_filters_model_restrictions_warning: ({ Link }) => (
        <React.Fragment>
            <Link>Condition Detail Notes</Link> &nbsp; on model restrictions exist for the selected
            condition/s. &nbsp;<Link>See details.</Link>
        </React.Fragment>
    ),
    pdf_report_modal_description_placeholder: 'Add optional description/notes here',
    pdf_report_modal_controls_label: 'Report Content',
    pdf_report_modal_filter_section_label: 'Filters',
    pdf_report_modal_cdn_section_label: 'Condition Detail Notes',
    pdf_report_modal_chart_section_label: 'Chart',
    'pdf_report_modal_data-table_section_label': 'Data Table',
    pdf_report_modal_chart_section_is_disabled_tooltip:
        'Visualizations must be enabled to select this option.',
    pdf_report_modal_email_label: 'Email to',
    pdf_report_modal_email_placeholder: 'Enter recipient email address',
    pdf_report_modal_email_note:
        'Emailed reports can only include the Filters and Chart sections. The Data Table and Condition Detail Notes sections are available in download mode only.',
    pdf_report_modal_email_section_disabled_tooltip:
        'This section is not included in emailed reports.',
    pdf_report_modal_sending_label: 'Please wait, the report is being sent...',
    pdf_report_modal_email_success: 'The report has been sent to %email%.',
    pdf_report_modal_email_retry_error: ({ Link }) => (
        <>
            Failed to send the report. You can try again, or <Link>download it</Link> instead.
        </>
    ),
    pdf_report_modal_email_final_error:
        "There's a problem sending the report on our side. Please try again later.",
    pdf_report_modal_generation_error:
        'Something went wrong while generating the report. Please try again.',

    filter_label_primary_entity: 'Conditions',
    filter_label_primary_entity_ght: 'Pathogens',
    filter_label_primary_entity_ght_with_tooltip: (Tooltip) => (
        <>
            Pathogens <span style={{ width: 20, display: 'inline-block' }} />
            <Tooltip
                title={
                    <div>
                        The pathogens below are being actively monitored but there is not enough
                        activity to model at this time.
                        <u>
                            <li>Novel Flu</li>
                            <li>Novel Sars</li>
                            <li>Ebola</li>
                            <li>Cholera</li>
                            <li>Polio</li>
                            <li>Meningitis (meningococcal)</li>
                        </u>
                    </div>
                }
            >
                <span>{infoIcon} Other pathogens</span>
            </Tooltip>
        </>
    ),
    filter_label_x_axis_dimension: 'Axis dimension:',
    filter_label_dimension: 'Dimension:',
    filter_label_map_detail_level: 'Map Detail',
    filter_label_map_type: 'Map Type',

    // Lists
    lists_my_lists_header: 'My Lists',
    lists_public_lists_header: 'Public Lists',
    lists_add_new: '+ New List',
    lists_manage_lists: 'Manage Lists',
    lists_add_to_list: 'Add To List',
    default_list_name: 'My Favorites',
    default_group_list_name: 'Group Content',
    lists_add_new_modal_title: 'New List',
    lists_edit_modal_title: 'Edit List',
    lists_remove_modal_title: 'Remove List',
    lists_remove_modal_question: 'Are you sure?',
    lists_remove_resource_modal_title: 'Remove Item',
    lists_unstar_resource_modal_title: 'Remove item from list',
    lists_add_new_tab: '+ New Tab',
    lists_make_list_private: 'Make list private',
    lists_make_list_public: 'Make list public',
    lists_make_list_default: 'Make list default',
    lists_make_list_non_default: 'Disable as default list',
    lists_subscribe_button: 'Subscribe',
    lists_unsubscribe_button: 'Unsubscribe',
    lists_list_is_empty:
        "This list doesn't have any items yet. You can add files and links provided by your Organization. Or add your favorite Data Explorer configurations.",

    resource_list_field_label_name: 'Name',
    resource_list_field_label_description: 'Description',

    arrow_chart_location_id_dimension: 'Locations',
    arrow_chart_primary_entity_dimension: 'Conditions',
    arrow_chart_cause_id_dimension: 'Conditions',
    arrow_chart_race_id_dimension: 'Races',

    [`bar_chart_${LOCATION_ID_KEY}_dimension`]: 'Location',
    [`bar_chart_${AGE_GROUP_ID_KEY}_dimension`]: 'Age',
    [`bar_chart_${GENDER_ID_KEY}_dimension`]: 'Sex',
    [`bar_chart_${YEAR_KEY}_dimension`]: 'Year',
    [`bar_chart_${DAY_KEY}_dimension`]: 'Day',
    [`bar_chart_${RACE_ID_KEY}_dimension`]: 'Race',
    [`bar_chart_${EDUCATION_ID_KEY}_dimension`]: 'Education',
    [`bar_chart_${VACCINE_STATUS_ID_KEY}_dimension`]: 'Vaccine Status',
    [`bar_chart_${VACCINATION_STATUS_ID_KEY}_dimension`]: 'Vaccination Status',
    [`bar_chart_${FORECAST_SCENARIO_ID_KEY}_dimension`]: 'Forecast Scenario',

    tooltip_label_year: 'Year',
    tooltip_label_day: 'Day',
    tooltip_label_location_id: 'Location',
    tooltip_label_comorbid_cause_id: 'Comorbid Condition',
    tooltip_label_age_group_id: 'Age',
    tooltip_label_gender_id: 'Sex',
    tooltip_label_metric_id: 'Unit',
    tooltip_label_measure_id: 'Measure',
    tooltip_label_cause_id: 'Cause',
    tooltip_label_forecast_scenario_id: 'Forecast scenario',
    tooltip_label_month_count: 'Study (Months)',
    tooltip_label_risk_exposure_id: 'Exposure',
    tooltip_label_facility_id: 'Facility',
    tooltip_label_industry_id: 'Industry',
    tooltip_label_response_id: 'Response',
    tooltip_label_ethnicity_id: 'Ethnicity',
    tooltip_label_indicator_id: 'Indicator',
    tooltip_label_employment_cohort_id: 'Employment Cohort',
    tooltip_label_employment_status_id: 'Employment Status',
    tooltip_label_portal_organization_id: 'Portal Organization',
    tooltip_label_race_id: 'Race',
    tooltip_label_antibiotic_class_id: 'Antibiotic Class',
    tooltip_label_counterfactual_id: 'Counterfactual',
    tooltip_label_pathogen_id: 'Pathogen',
    tooltip_label_education_id: 'Education',
    tooltip_label_infectious_syndrome_id: 'Infectious Syndrome',
    [`tooltip_label_${PRIMARY_ENTITY_ID_LOCALIZATION_KEY}`]: 'Condition',
    [`tooltip_label_${VACCINE_STATUS_ID_KEY}`]: 'Vaccine Status',
    [`tooltip_label_${SEVERITY_ID_KEY}`]: 'Severity',
    [`tooltip_label_${OUTCOME_ID_KEY}`]: 'Dengue Case Outcome',
    [`tooltip_label_${INFECTION_TYPE_ID_KEY}`]: 'Infection Type',
    [`tooltip_label_${VACCINATION_STATUS_ID_KEY}`]: 'Vaccination Status',
    [`tooltip_label_${PAYER_ID_KEY}`]: 'Payer',
    [`tooltip_label_${TOC_ID_KEY}`]: 'Type Of Care',
    [`tooltip_label_${CONSTANT_VACCINATION_ID_KEY}`]: 'Vaccine Effectiveness',
    [`tooltip_label_${EXPANDED_WOLBACHIA_ID_KEY}`]: 'Wolbachia Effectiveness',
    [`tooltip_label_${CRO_ID_KEY}`]: 'Granular Resolution',

    granular_resolution_methodology_info_title: 'Granular Resolution Methodology Info',
    granular_resolution_methodology_info_description:
        "The Granular Resolution pipeline builds on IHME estimates by utilizing electronic health records or cancer registries to disaggregate broad cause-specific estimates into further granular conditions. By focusing on ICD-10 codes from EHR and molecular subtype from cancer registries, the pipeline calculates specific proportions for detailed conditions across age and sex strata. These proportions are then applied to standard IHME estimates allowing for more precise clinical insights. While input sources vary between EHR and registry data, the subsequent calculation steps remain similar, ensuring a standardized approach to refining IHME values. This methodology allows the GBD framework to meet demand for high-resolution data while maintaining the integrity and rigor of IHME's research.",

    population_1: 'Population',
    life_expectancy_1: 'Life Expectancy',
    covid19_1: 'Covid-19',

    yes_button: 'Yes',
    ignore_and_continue_button: 'Ignore and Continue',
    disable_charts_and_continue_button: 'Disable Charts and Continue',
    continue_anyway_button: 'Continue Anyway',

    data_explorer_select_data: 'Select Data',
    data_explorer_select_all: 'Select All',
    data_explorer_select_more: 'Select More...',
    data_explorer_remove: 'Remove',

    dex_query_builder_title: 'Query Builder',
    dex_query_builder_manual_filters: 'Manual Filters',
    dex_query_builder_mode_selector_label: 'Query builder mode',
    dex_query_builder_collapse_label: 'Collapse Query Builder',
    dex_query_builder_collapse_disabled_label:
        'To collapse the Query Builder, query the data first',
    dex_query_builder_expand_label: 'Expand Query Builder',
    dex_query_builder_new_search_label: 'New search',
    dex_query_builder_applied_filters: 'Applied filters:',
    dex_query_builder_collapsed_label: 'Query Builder',
    dex_chat_beta_badge: 'Beta',
    dex_chat_data_collection_auto_label: 'Auto',
    dex_chat_data_collection_auto_description: 'finds a Data Collection for you',
    dex_chat_data_collection_auto_placeholder: 'Data Collection',
    dex_chat_data_collection_switch_prompt: 'Try another dataset that could answer this:',
    dex_chat_input_placeholder:
        'Example: Mortality rate for cardiovascular diseases in Poland, 1990-2022, females 30-50.',
    dex_chat_welcome_message_intro:
        'Tell me what you are looking for in plain language and I will configure the filters for you. Name a condition, risk factor, or ICD code - everything else is optional.',
    dex_chat_welcome_message_examples: [
        '- **Query data** - "Deaths from ischemic heart disease in Texas, 2019"',
        '- **Rank** - "Top 5 causes of death in Nigeria"',
        '- **Aggregate** - "Total deaths in Kenya across 2010-2020", "Average malaria prevalence by sex"',
        '- **Break down by a dimension** - "Malaria prevalence by age group"',
        '- **Groups & presets** - "HIV deaths in all Sub-Saharan African countries", "all Texas counties", "Deaths from all level 2 causes", "Select All 5 Year Age Bands"',
        '- **ICD codes** - "Prevalence of 775.1"',
    ].join('\n'),
    dex_chat_welcome_message_pharma_example:
        '- **Drugs & companies** - "Conditions treated by Ozempic", "What does Novo Nordisk target"',
    dex_chat_welcome_message_refine_example: [
        '- **Refine step by step** - "add Kenya", "only females", "drop under 5"',
        '- **Pick the chart** - "show it on a map", "switch to a line chart", "TB deaths in Brazil as a treemap"',
    ].join('\n'),
    dex_chat_welcome_message_defaults:
        'Only the condition is required - location, year, age, gender and measure fall back to sensible defaults (Global, all years, All Ages, Both).',
    dex_chat_welcome_message_auto_collection_note:
        "Pick which dataset I search with the **Data Collection** selector below, or leave it on **Auto** and I'll find the right one for you.",
    dex_chat_welcome_message_manual_filters_note:
        'To set filters by hand, switch to **Manual Filters** or use the edit icon on any return.',
    dex_chat_beta_disclaimer:
        'By using this beta, you agree your conversations will be stored and may be reviewed to improve the tool.',
    dex_chat_analyzing_status: 'Analyzing requested parameters',
    dex_chat_analyzing_word_parsing: 'Parsing your question',
    dex_chat_analyzing_word_untangling: 'Untangling the details',
    dex_chat_analyzing_word_cross_referencing: 'Cross-referencing filters',
    dex_chat_analyzing_word_sifting: 'Sifting through the data',
    dex_chat_analyzing_word_triangulating: 'Triangulating results',
    dex_chat_analyzing_word_mapping_query: 'Mapping your query',
    dex_chat_analyzing_word_piecing_together: 'Piecing it together',
    dex_chat_analyzing_word_narrowing: 'Narrowing the results',
    dex_chat_analyzing_word_still_working: 'Still working on it',
    dex_chat_analyzing_word_taking_longer: 'This one’s taking a bit longer',
    dex_chat_analyzing_word_still_crunching: 'Still crunching',
    dex_chat_analyzing_word_almost_there: 'Almost there',
    dex_chat_analyzing_word_patience: 'Thanks for your patience',
    dex_chat_stop_label: 'Stop generating',
    dex_chat_stop_tooltip: 'Stop',
    dex_chat_prompt_label: 'AI query prompt',
    dex_chat_conversation_label: 'AI chat conversation',
    dex_chat_error_default: 'I couldn’t complete your search. Please try again in a moment.',
    dex_chat_edit_filters_label: 'Edit filters',
    dex_chat_manual_filters_linked: 'Linked',
    dex_chat_manual_filters_share_error:
        'Your Manual Filters changes couldn’t be shared with the chat, so this answer doesn’t include them.',
    dex_chat_manual_filters_pending_note:
        'These changes will be shared with the chat when you send your next message.',
    dex_chat_manual_filters_sync_on_label:
        'Linked: once the chat has set the filters, your edits in Manual Filters are sent with your next message. Click to unlink.',
    dex_chat_manual_filters_sync_off_label:
        'Unlinked: Manual Filters changes aren’t shared with the chat. Click to link.',
    dex_chat_switch_to_manual_label: 'Continue in Manual Filters',
    dex_chat_thumbs_up_label: 'Give positive feedback',
    dex_chat_thumbs_down_label: 'Give negative feedback',
    dex_chat_feedback_like_title: 'Tell us what you liked',
    dex_chat_feedback_dislike_title: 'Tell us what went wrong',
    dex_chat_feedback_details_label: 'Details (Optional)',
    dex_chat_feedback_disclaimer:
        'Submitting this report will send the entire current conversation for future improvements to our models.',
    dex_chat_feedback_submitting: 'Submitting…',
    dex_chat_feedback_retry: 'Retry',
    dex_chat_feedback_error_default: 'Couldn’t submit feedback. Please try again.',
    dex_chat_feedback_error_missing_context:
        'Couldn’t submit feedback because the chat session is unavailable.',
    dex_chat_feedback_success_title: 'Feedback submitted',
    dex_chat_feedback_success_message: 'Your input helps us improve the results.',
    dex_chat_history_label: 'Chat history',
    dex_chat_new_chat_label: 'New chat',
    dex_chat_history_close_label: 'Close chat history',
    dex_chat_history_search_placeholder: 'Search in chat history',
    dex_chat_history_search_clear_label: 'Clear search',
    dex_chat_history_search_empty: 'No chats match your search.',
    dex_chat_history_empty: 'No previous chats yet.',
    dex_chat_history_load_failed: 'Could not load chat history. Please try again.',
    dex_chat_history_loading: 'Loading chat history',
    dex_chat_history_untitled: 'Untitled chat',
    dex_chat_history_group_today: 'Today',
    dex_chat_history_group_yesterday: 'Yesterday',
    dex_chat_history_group_last_week: 'Last week',
    dex_chat_history_group_older: 'Older',
    dex_chat_history_item_menu_label: 'Chat actions',
    dex_chat_history_rename: 'Rename',
    dex_chat_history_delete_modal_title: 'Delete item?',
    dex_chat_history_delete_modal_description: 'This action is permanent and cannot be undone.',
    dex_chat_history_rename_modal_title: 'Rename chat',
    dex_chat_history_rename_modal_label: 'Chat name',
    dex_chat_history_action_success_toast: 'Chat %action%',
    dex_chat_history_action_error_toast: 'The chat was not %action%. Please retry.',

    dex_chat_ranking_title_top: 'Top %limit% %entity% by %metric% (highest first)',
    dex_chat_ranking_title_bottom: 'Bottom %limit% %entity% by %metric% (lowest first)',
    dex_chat_ranking_entity_level: '%entity% level %level%',
    dex_chat_ranking_entity_named_level: '%level% %entity%',
    dex_chat_aggregation_title: '%function% %metric% by %groups%, across %dimensions%',
    dex_chat_aggregation_title_total: '%function% %metric% across %dimensions%',
    dex_chat_aggregation_dimension: '%name% (%values%)',
    dex_chat_aggregation_dimension_truncated: '%name% (%values% and %count% more)',
    dex_chat_aggregate_function_sum: 'Total',
    dex_chat_aggregate_function_mean: 'Average',
    dex_chat_table_entries: '<span><b>%shown%</b> of <b>%total%</b> entries</span>',
    dex_chat_table_loading: 'Fetching results',
    dex_chat_table_error: 'Couldn’t load these results. Please try again.',
    dex_chat_query_result_unsupported: 'These results can’t be displayed yet.',
    dex_chat_restricted_prompt_generic:
        'Some results are restricted by your current license. Would you like to contact IHME to extend your license and access these results?',
    dex_chat_restricted_prompt_specific:
        'The hidden results include %names%. Would you like to contact IHME to extend your license and access these results?',

    data_explorer_filters_amount_warning:
        'The scope of data is large and may impact performance of the Data Explorer. You can ignore this warning and proceed to run the query or cancel.',
    data_explorer_filters_chart_limit_exceeded:
        'The scope of data is too large to visualize in charts. As a result, charts will be disabled for this query.',
    data_explorer_filters_limit_exceeded:
        'The scope of data is too large to query. Try reducing the number of filter selections.',
    data_explorer_charts_data_limit_receded:
        'Charts have been re-enabled based on your new data selection.',
    data_explorer_charts_data_limit_exceeded:
        'The scope of data is too large to visualize. Reduce the number of filter selections to re-enable charts.',

    map_detail_level_0: 'National',
    map_detail_level_1: 'Subnational',
    map_detail_level_2: 'Detail Subnational',
    map_detail_level_3: 'Borough',

    treemap_detail_level_1: 'Level 1',
    treemap_detail_level_2: 'Level 2',
    treemap_detail_level_3: 'Level 3',

    chart_axis_label_day: 'Dates',
    chart_axis_label_year: 'Years',
    chart_axis_label_start_date: 'Weeks',
    chart_axis_label_month_count: 'Study (Months)',
    chart_axis_label_location_id: 'Locations',
    chart_axis_label_age_group_id: 'Ages',
    chart_axis_label_gender_id: 'Sexes',
    chart_axis_label_race_id: 'Race',
    [`chart_axis_label_${CRO_ID_KEY}`]: 'Granular Resolution',

    sev_records_long_metric_3: 'Exposure per 100',
    ght_records_measure_2: 'DALYs per 100k',
    ght_records_measure_10005: 'Cases',

    content_no_longer_available_title: 'Content is No Longer Available',
    content_no_longer_available_message: (
        <>
            This list item relies on content that has been removed or expired.
            <br /> For questions/concerns please reach out to <ServicesLink />.
        </>
    ),

    gbd_compare_access_tooltip:
        'To access GBD Compare use the following login:<br/><div><b>Username:</b> ihmeclient</div><div><b>Password:</b> discoverydatum</div>',
    gbd_results_access_tooltip:
        'To access GBD Results use the following login:<br/><div><b>Username:</b> ihmeclient</div><div><b>Password:</b> discoverydatum</div>',

    [`data_tool_data_${DATA_EXPLORER_PATH}`]: 'GBD',
    [`data_tool_data_${COVID_19_EXPLORER_PATH}`]: 'Covid-19',
    [`data_tool_data_${MORBIDITY_EXPLORER_PATH}`]: 'Multimorbidity',
    [`data_tool_data_${EDP_EXPLORER_PATH}`]: 'EDP',
    [`data_tool_data_${SIMULATION_EXPLORER_PATH}`]: 'Simulation',
    [`data_tool_data_${HEALTH_THREATS_METRICS_PATH}`]: 'Health Threats',
    [`data_tool_data_${DENGUE_SCENARIOS_PATH}`]: 'Dengue Scenarios',

    data_not_shared_title: 'Data has not been shared with your organization.',
    data_tool_not_shared_message: ({ dataToolData, ServicesLink }) => (
        <>
            {dataToolData} data is no longer shared with your organization. To gain access please
            reach out to <ServicesLink />.
        </>
    ),
    no_shared_collections_message: ({ ServicesLink }) => (
        <>
            If you are interested in licensing GBD data from IHME please reach out to{' '}
            <ServicesLink />.
        </>
    ),

    [`data_tool_${DATA_EXPLORER_PATH}`]: 'Data Explorer',
    [`data_tool_${COVID_19_EXPLORER_PATH}`]: 'Covid-19 Explorer',
    [`data_tool_${MORBIDITY_EXPLORER_PATH}`]: 'Multimorbidity Explorer',
    [`data_tool_${EDP_EXPLORER_PATH}`]: 'Early Detection Program Explorer',
    [`data_tool_${SIMULATION_EXPLORER_PATH}`]: 'Simulation Explorer',
    [`data_tool_${HEALTH_THREATS_METRICS_PATH}`]: 'Health Threats',
    [`data_tool_${DENGUE_SCENARIOS_PATH}`]: 'Dengue Scenarios',

    start_survey_button_label: 'Start Survey',
    survey_banner_title: 'Got a Minute? Share Your Thoughts!',

    export_trigger_line: 'Timeline Chart',
    export_trigger_map: 'Map Chart',
    export_trigger_bar: 'Bar Chart',
    export_trigger_treemap: 'Treemap Chart',
    export_trigger_scatter_map: 'Map Chart',
    export_trigger_arrow: 'Arrow Chart',
    export_trigger_data_table: 'Data',
    export_trigger_pdf_report: 'Report',

    data_explorer_share_permalink_btn: 'Share',
    share_permalink_copy_btn: 'Copy Permalink',
    share_permalink_title: 'Share Permalink',
    share_permalink_input_label: 'Name Permalink',
    share_permalink_nav_link_label: 'Permalink',
    share_permalink_message:
        'Data resources can only be shared with members inside your organization. A copy of the Permalink can be found in the Permalink list, in Favorite Content.',
    permalink_unavailable_data: 'Underlying data is unavailable',

    cause_coprevalence_records_tooltip:
        'Estimates of current co-prevalence of disease, based on most-recent year available from the Global Burden of Disease Study.',
    projected_comorbidity_causes_outcome_records_tooltip:
        'Future projections of new cases of co-occurring disease among a hypothetical population and duration that you can specify.',

    cause_associated_coprevalence_records_tooltip:
        'Estimates of current co-prevalence of disease, based on most-recent year available from the Global Burden of Disease Study.',
    cause_associated_comorbidity_study_coefficients_tooltip:
        'Future projections of new cases of co-occurring disease among a hypothetical population and duration that you can specify.',

    comorbidity_type_dropdown_label: 'Data Type',
    comorbidity_correlation_factor_label: 'Relative risk of co-occurrence of diseases',
    comorbidity_participants_number_label: 'Size Of Synthetic Population',
    comorbidity_condition_label: 'Underlying Condition',
    comorbidity_condition_placeholder: 'Search',

    comorbidity_all_tab: 'Co-Occurrence',
    comorbidity_all_tab_select_data:
        'Independent Co-occurrence of diseases estimated using the Global Burden of Disease Study',
    comorbidity_all_tab_tooltip: (
        <div>
            <li>
                Using this data set, a user can view estimates, by age and sex, of the co-occurrence
                (also known as co-prevalence) and incidence of a wide range of disease pairs among
                the general population in the United States based on data from the Global Burden of
                Disease.
            </li>
            <li>
                The estimates in this data set provide a background rate of co-occurrence assuming{' '}
                <i>no increased risk</i> of a comorbid diseases among those with an underlying
                disease. These estimates assume independence between the occurrence of pairs of
                diseases.
            </li>
            <li>
                The rates of each disease in the selected population determine the estimated
                co-occurrence rate.
            </li>
            <li>
                Co-occurrence can be considered a plausible lower bound estimate of comorbidity for
                any two conditions, excluding cases where a first disease confers a protective
                effect for the second disease.
            </li>
        </div>
    ),

    comorbidity_associated_tab: 'Comorbidity',
    comorbidity_associated_tab_select_data:
        'Comorbidity estimated using the Global Burden of Disease Study and representative (USA) population survey data',
    comorbidity_associated_tab_tooltip: (
        <div>
            <li>
                Using this data set, a user can view estimates, by age and sex, of comorbid
                prevalence for approximately 500 disease pairs among the general population in the
                United States based on information from the Global Burden of Disease Study and the
                Medical Expenditures Panel Survey (MEPS), a nationwide study of health care use and
                expenditures conducted by the Agency for Healthcare Research and Quality (AHRQ)
            </li>
            <li>
                The estimates in this data set account for the increased risk of comorbid diseases
                conditional on having an underlying disease.
            </li>
            <li>
                The rates of each disease in the selected population and the probability that they
                co-occur as observed among subjects of the MEPS panel survey determine the estimated
                comorbidity rate.
            </li>
        </div>
    ),

    comorbidity_all_summary:
        'Among an estimated %prevalenceCauseValue% individuals, all ages, with %primaryCondition% in %location%, an estimated %coPrevalencePercent%% (%value% individuals) have %comorbidCondition% as a comorbidity. Results for queried age and sex groups are shown in table below.',
    comorbidity_associated_summary:
        'Among an estimated %prevalenceCauseValue% individuals aged 20+ with %primaryCondition% in %location%, an estimated %coPrevalencePercent%% (%value% individuals) have %comorbidCondition% as a comorbidity. Results for queried age and sex groups are shown in table below.',
    comorbidity_summary_annotation: '*Uncertainty available for all values',

    comorbidity_study_coefficients_summary:
        'Among patients with %primaryCondition%, all ages, in %location%, an estimated %value% per 100,000 would be expected to develop %comorbidCondition% over a 12-month period. Results for queried age and sex groups are shown in table below.',

    comorbidity_summary_gender_1: 'males',
    comorbidity_summary_gender_2: 'females',
    comorbidity_summary_gender_3: 'individuals',

    dengue_scenarios_about_tab: 'About the data',
    dengue_scenarios_explorer_tab: 'Data Explorer',
    dengue_forecast_scenarios_selector_title: 'Scenarios Explorer',
    dengue_averted_outcome_title_averted_by: 'averted by',
    dengue_averted_outcome_title_death_outcome: 'Deaths',
    dengue_averted_outcome_title_other_outcome: 'Cases',

    dengue_scenarios_about_section_title: (
        <>
            <em>Modeling TAK-003 impact in combination with vector control</em>: Project Context
        </>
    ),
    dengue_scenarios_about_section_overview_title: 'Overview',
    dengue_scenarios_about_section_overview_p1:
        'The Institute for Health Metrics and Evaluation (IHME) aims to improve health outcomes globally by making available the highest-quality information on population health, its determinants, and the performance of health systems. In furthering that purpose, IHME Client Services partnered with Takeda to estimate the burden of dengue in Brazil and quantify the impact of TAK-003 vaccine administration in combination with Wolbachia implementation across subnational locations in Brazil, as well as at the national level.',
    dengue_scenarios_about_section_overview_p2: (
        <>
            Half of the world's population is estimated to be at risk of dengue virus (DENV)
            infection. While the health burden of other leading communicable diseases has decreased
            over the past 20 years, DENV burden has increased. Dengue virus is transmitted primarily
            by <em>Aedes aegypti</em> mosquitoes. Due to their preference for urban and peri-urban
            environments, the range of DENV is forecasted to increase over the next 30 years.
            Historically, there have been limited interventions proven to halt DENV transmission,
            but several emerging approaches show promise. As DENV epidemics continue to occur
            globally it is critical to evaluate the integrated impact of a variety of DENV
            intervention tools. This project aimed to address this evidence gap by utilizing a
            compartmentalized model, incorporating in the most up-to-date Brazil data, and defining
            key model parameters informed by literature and expert guidance.
        </>
    ),
    dengue_scenarios_about_section_tool_title: 'What you will find in this tool',
    dengue_scenarios_about_section_tool_p1:
        'IHME developed this tool as a means for Takeda stakeholders to explore and analyze a subset of the modeled outputs, which constitute highly voluminous data sets which can be time-consuming and resource-intensive to analyze independently.',
    dengue_scenarios_about_section_tool_p2: (
        <>
            In the Data Explorer tab within this tool, you can query model results by the following
            filters and visualize the results over time (simulated years) in the{' '}
            <strong>Timeline</strong> chart, over space in the <strong>Map</strong> chart, and
            compare across key categories in the <strong>Bar</strong> chart.
        </>
    ),
    dengue_scenarios_about_section_tool_stratification:
        'Data stratification options and descriptions:',
    dengue_scenarios_about_section_tool_guidance: (UnderlinedText) => (
        <>
            Additional information, including a step-by-step overview of how to set up your query,
            can be found in the <UnderlinedText>User Interface Guidance.pptx</UnderlinedText>{' '}
            document available from your Dashboard (click "Dashboard" in the left-hand navigation
            bar, then look for "Group Content" in the bottom-center of the page).
        </>
    ),
    dengue_scenarios_about_section_scenarios_title: 'About the scenarios',
    dengue_scenarios_about_section_scenarios_intro:
        'In this tool, you can view the results described above under multiple scenarios. These are described in greater detail in the technical documentation for the project, but brief summaries are provided here for convenience.',
    dengue_scenarios_about_section_baseline_title: 'Baseline scenario',
    dengue_scenarios_about_section_baseline_desc:
        'The Baseline scenario models DENV transmission and outcomes assuming neither vaccine nor Wolbachia intervention.',
    dengue_scenarios_about_section_vaccine_title: 'Vaccine scenarios',
    dengue_scenarios_about_section_vaccine_p1: (
        <>
            The "<strong>Constant vaccination</strong>" scenario assumes a gradual vaccine rollout,
            starting at 36.6% coverage among 10–14-year-olds and increasing over 5 years to achieve
            90% coverage. Vaccine coverage in this scenario is based on HPV 1st dose coverage in
            Brazil, and assumes full compliance of 2nd dose.{' '}
            <strong>This is the vaccination scenario included in the tool.</strong> Within this
            vaccination scenario, you can view results for an assumed "high," "low," and "mean"
            vaccine effectiveness.
        </>
    ),
    dengue_scenarios_about_section_vaccine_p2: (
        <>
            The "Rapid Ramp-up" scenario assumes a more aggressive vaccine rollout, achieving 90%
            coverage in 10–14-year-olds in the 1<sup>st</sup> year. These outputs can be consulted
            in provided flat files.
        </>
    ),
    dengue_scenarios_about_section_wolbachia_title: 'Wolbachia scenarios',
    dengue_scenarios_about_section_wolbachia_p1:
        'In the tool, you will be able to view and compare vaccine-only scenarios or combine any vaccine scenario with any Wolbachia scenario-this is called an "integrated scenario." No outputs for Wolbachia-only scenarios are provided.',
    dengue_scenarios_about_section_wolbachia_p2:
        'Wolbachia scenarios considered as part of the project, in terms of geographical area for Wolbachia application, are "Base case," "Medium," and "Expanded." These are defined below. Wolbachia scenario outputs included in this tool are all specific to the "Expanded" scenario. Within this scenario, outputs are provided under three differing assumptions about the effectiveness of the Wolbachia intervention: "low", "high", and "mean" effectiveness. In the tool, you will be able to specify for which Wolbachia effectiveness assumption you would like to view results.',
    dengue_scenarios_about_section_wolbachia_effectiveness: 'Wolbachia effectiveness scenarios:',
    dengue_scenarios_about_section_wolbachia_deployment: 'Wolbachia deployment scenarios:',
    dengue_scenarios_about_section_methods_title: 'A note on methods',
    dengue_scenarios_about_section_methods_p1:
        'Building on the structure proposed in previous work by Takeda, a deterministic compartmental model of dengue transmission was developed with an age-structured host population, explicitly modeled vector population, all four dengue serotypes, cross-protection and differential risk of clinical or hospitalized disease for secondary infections. The model can be fitted to empirical data, including age-specific and seasonal incidence rates of symptomatic dengue.',
    dengue_scenarios_about_section_methods_p2:
        'The modeling of vaccination impact is designed to reflect the profile of the new dengue vaccine TAK-003, as informed by the pivotal clinical trial DEN-301. Additionally, the model allows for a boosting of vaccine-derived protection with each breakthrough infection.',
    dengue_scenarios_about_section_methods_p3:
        'The methodological approach builds on the DENV transmission model previously used by Takeda, with the adaptations and alterations designed collaboratively by IHME experts and by Takeda. Details on these adaptations, modeling techniques, and input data sources can be found in the Technical Report.',
    dengue_scenarios_about_section_resources_title: 'When to consult additional resources',
    dengue_scenarios_about_section_resources_p1: `As part of this Project, IHME has generated a wide range of outputs for Takeda's use. This tool was developed to provide you with a foundational set of modeled outputs and a wide range of interactive visualization options to analyze and display those outputs.`,
    dengue_scenarios_about_section_resources_p2:
        'Depending on your analysis needs, you may wish to consult other deliverables from the Project. The table below shows the deliverables that can best serve different use cases.',
    dengue_scenarios_about_list_infection_results: 'Infection results.',
    dengue_scenarios_about_list_infection_results_desc: (
        <>
            You may select the type of dengue outcome they are interested in. Infection results are
            stratified by{' '}
            <strong>Severity (moderate, severe, any), Symptomatic (yes, no, any),</strong> and{' '}
            <strong>
                Outcome (non-hospitalized case, hospitalized case, death, any infection).
            </strong>
        </>
    ),
    dengue_scenarios_about_list_asymptomatic_desc: (
        <>
            Asymptomatic infections can only result in <em>non-hospitalized</em> outcomes<em>.</em>{' '}
            That is to say, if "No" is selected in the <strong>Symptomatic</strong> dropdown, then
            you may only select "Non-hospitalized case" from the <strong>Outcome</strong> dropdown.
        </>
    ),
    dengue_scenarios_about_list_apparent: 'Apparent',
    dengue_scenarios_about_list_apparent_desc: 'You can select apparent, inapparent, or all cases.',
    dengue_scenarios_about_list_ages: 'Ages',
    dengue_scenarios_about_list_ages_desc:
        'You can select All Ages or 5-year age bins from 0 to 70+.',
    dengue_scenarios_about_list_locations: 'Locations',
    dengue_scenarios_about_list_locations_desc:
        'You can view results for all of Brazil, for each state within Brazil, for all Wolbachia locations combined.',
    dengue_scenarios_about_list_infection_type: 'Infection type',
    dengue_scenarios_about_list_infection_type_desc:
        'You can view all infection types or stratify results by Primary, Secondary, and Third or greater infections.',
    dengue_scenarios_about_list_vaccination_status: 'Vaccination status',
    dengue_scenarios_about_list_vaccination_status_desc: (
        <>
            You can view all vaccination statuses or stratify results by populations modeled as Not
            vaccinated, Seronegative at vaccination, Seropositive at vaccination, or Vaccinated
            (seropositive <em>or</em> seronegative at vaccination).
        </>
    ),
    dengue_scenarios_about_list_years: 'Years',
    dengue_scenarios_about_list_years_desc:
        'User can view results spanning all 45 years from the intervention start date, or select a subset of years i.e. view results in years 25–35 only after assumed intervention start.',
    dengue_scenarios_about_wolbachia_low: (
        <>
            <strong>Low</strong> assumes a 65.3% reduction in reported cases within Wolbachia
            intervention areas.
        </>
    ),
    dengue_scenarios_about_wolbachia_mean: (
        <>
            <strong>Mean</strong> assumes a 77.1% reduction in reported cases within Wolbachia
            intervention areas.
        </>
    ),
    dengue_scenarios_about_wolbachia_high: (
        <>
            <strong>High</strong> assumes a 84.9% reduction in reported cases within Wolbachia
            intervention areas.
        </>
    ),
    dengue_scenarios_about_wolbachia_base_case: (
        <>
            <strong>Base case</strong> assumes application of Wolbachia in 7 large cities/centers
            identified as "high priority" based on population size and dengue disease burden: São
            Paulo, Fortaleza, Campo Grande, Goiania, Belo Horizonte, Manaus and Niteroi. Wolbachia
            application in these locations would cover around 23 million people in total.
        </>
    ),
    dengue_scenarios_about_wolbachia_medium: (
        <>
            <strong>Medium</strong> Wolbachia scenario assumes application of Wolbachia in 25
            locations consisting of the 7 Base case locations plus the rest of the metropolitan area
            in each city that each Base case location belongs to, covering about 35 million people.
        </>
    ),
    dengue_scenarios_about_wolbachia_expanded: (
        <>
            <strong>Expanded</strong> Wolbachia scenario represents and ambitious scale of Wolbachia
            application, including locations in Small scenario and expanding to all qualifying
            municipalities surrounding those locations, which would cover around 70 million people.{' '}
            <strong>
                This scenario is the one represented by the Wolbachia scenario options in this tool.
            </strong>
        </>
    ),

    // Table data
    dengue_scenarios_about_table_header_deliverable: 'Deliverable',
    dengue_scenarios_about_table_header_use_cases: 'Use cases served',
    dengue_scenarios_about_table_visualization_tool: 'Visualization tool',
    dengue_scenarios_about_table_output_flat_files: 'Output flat files',
    dengue_scenarios_about_table_configurable_code_file: 'Configurable code file',
    dengue_scenarios_about_table_use_case_1: 'Quick introduction to project results',
    dengue_scenarios_about_table_use_case_2: 'Dynamic demo of results for less-technical audience',
    dengue_scenarios_about_table_use_case_3:
        'Easy creation of charts and visuals for internal sharing and reporting',
    dengue_scenarios_about_table_use_case_4: 'In-depth analysis of results from topline scenarios',
    dengue_scenarios_about_table_use_case_5: 'Programmatic analysis of project outputs',
    dengue_scenarios_about_table_use_case_6:
        'Creation of custom visualizations (e.g., with different color scheme)',
    dengue_scenarios_about_table_use_case_7:
        'Access to expanded set of scenarios generated within project (e.g., pre-run sensitivity analyses)',
    dengue_scenarios_about_table_use_case_8:
        'Re-run model to explore differences in results if select scenario parameters are adjusted',
    dengue_scenarios_any_vaccination_status: 'Any vaccination status',
    dengue_scenarios_severity: 'severity',
    dengue_scenarios_infection: 'infection',
    data_table_filter_select_columns: 'Select Columns',

    export_pdf_button_disabled_tooltip:
        'The scope of data is too large to export for PDF. Try reducing the number of filter selections.',

    export_excel_button: 'Excel',
    export_data_excel_button: 'Data .xlsx',
    export_excel_button_disabled_tooltip:
        'The scope of data is too large to export in Excel. Try reducing the number of filter selections.',

    export_csv_button: 'CSV',
    export_data_csv_button: 'Data .csv',
    export_csv_button_disabled_tooltip:
        'The scope of data is too large to export in CSV. Try reducing the number of filter selections.',

    export_button: 'Export',
    export_image_button: 'Chart image',
    export_pdf_button: 'PDF report',
    export_image_disabled_tooltip: 'Chart image export is not available for Data Table view.',
    export_failed: 'Unable to complete export. Please try again.',

    data_explorer_widget_description: 'Visualize estimates in tables and charts.',
    data_explorer_widget_explore: 'Explore',

    password_protected_tool_text:
        'Please enter the Multiple Myeloma Simulation Explorer password to access the tool.',

    share_more: 'Share More:',

    increased: 'increased',
    decreased: 'decreased',
    not_changed: 'not changed',

    total: 'Total',

    prob_level_1: 'Level 1',
    prob_level_2: 'Level 2',
    prob_level_3: 'Level 3',
    prob_level_4: 'Level 4',
    prob_level_5: 'Level 5',
    prob_level_6: 'Level 6',
    prob_level_7: 'Level 7',
    prob_level_8: 'Level 8',

    chart_error_ght_no_data:
        'Currently monitoring %pathogen% in %location%; there is not enough activity to model.',

    smart_option_causes_level_2: 'Select All Causes Level 2',
    smart_option_causes_level_3: 'Select All Causes Level 3',
    smart_option_causes_level_4: 'Select All Causes Level 4',

    dengue_1: 'Dengue',

    chart_gif_animation_in_progress: 'Please wait, GIF animation is being generated...',

    age_group_above: 'and above',

    rotate_device_notice_title: 'Rotate for a better view',
    rotate_device_notice_message:
        '%tool% works best in landscape. Rotate your device to give the charts more room.',
    rotate_device_notice_button: 'Got it',
};
